import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  pickableMarkerIcons,
  type MarkerIcon,
  type MarkerIconCategory,
} from '@/map/marker-icon-catalog';
import { cn } from '@/lib/utils';

interface IconPickerProps {
  /** Currently selected icon key, or undefined for none. */
  value: string | undefined;
  /** Called with the chosen icon key. */
  onChange: (key: string) => void;
  className?: string;
}

/** Case-insensitive substring match over an icon's label and key. */
function matchesQuery(icon: MarkerIcon, term: string): boolean {
  if (!term) return true;
  const q = term.toLowerCase();
  return icon.label.toLowerCase().includes(q) || icon.key.toLowerCase().includes(q);
}

/** Group icons by category, preserving catalog order. */
function groupByCategory(icons: readonly MarkerIcon[]): Map<MarkerIconCategory, MarkerIcon[]> {
  const groups = new Map<MarkerIconCategory, MarkerIcon[]>();
  for (const icon of icons) {
    const bucket = groups.get(icon.category);
    if (bucket) bucket.push(icon);
    else groups.set(icon.category, [icon]);
  }
  return groups;
}

/** A single selectable glyph tile. */
function IconTile({
  icon,
  selected,
  onSelect,
}: {
  icon: MarkerIcon;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  const { Icon } = icon;
  return (
    <button
      type="button"
      title={icon.label}
      aria-label={icon.label}
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex h-9 w-9 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-echo-yellow',
        selected
          ? 'border-echo-yellow/50 bg-echo-yellow/15 text-echo-yellow'
          : 'border-echo-border text-muted-foreground hover:bg-white/[0.06] hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

/**
 * Grid picker for a location's marker glyph. Icons come from the curated
 * `pickableMarkerIcons` catalog (single source of truth), grouped by category
 * with a substring filter. Controlled: emits the chosen icon key via `onChange`.
 */
export function IconPicker({ value, onChange, className }: IconPickerProps): React.JSX.Element {
  const [filter, setFilter] = useState('');

  const groups = useMemo(() => {
    const term = filter.trim();
    return groupByCategory(pickableMarkerIcons.filter((icon) => matchesQuery(icon, term)));
  }, [filter]);

  const empty = groups.size === 0;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter icons…"
          className="w-full rounded-md border border-echo-border bg-[#111] py-1.5 pl-8 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus-visible:border-echo-yellow/40 focus-visible:outline-none"
        />
      </div>

      <div className="max-h-56 overflow-y-auto pr-1">
        {empty && <p className="px-1 py-2 text-xs text-muted-foreground">No icons match “{filter}”.</p>}
        {[...groups.entries()].map(([category, icons]) => (
          <section key={category} className="mb-2">
            <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
              {category}
            </p>
            <div className="grid grid-cols-6 gap-1.5">
              {icons.map((icon) => (
                <IconTile
                  key={icon.key}
                  icon={icon}
                  selected={icon.key === value}
                  onSelect={() => onChange(icon.key)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
